import { Form, Formik } from 'formik';
import { Category } from '../../../models/Category/Category';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TreeSelect } from 'antd';
import { InputField } from '../../components/InputField/InputField';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { Col, FormGroup, FormLabel, Row } from 'react-bootstrap';
import { ErrorMessageLabel } from '../../components/ErrorMessageLabel/ErrorMessageLabel';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useMemo, useRef, useState, useEffect } from 'react';
import style from './create-category.module.scss';
import { FaPlus as PlusIcon, FaTimes as TrashIcon } from 'react-icons/fa';
import { Media } from '../../../models/Media/Media';
import { MediaLibrarySelector } from '../../components/MediaLibrarySelector/MediaLibrarySelector';

const categoryValidationSchema = Yup.object({
  parent_id: Yup.number().nullable(),
  name: Yup.string().required('Kategorija mora imati ime'),
  price: Yup.number().required('Unesite cenu proizvoda'),
  penalty_price: Yup.number().required('Unesite kaznenu cenu'),
  cover_image_id: Yup.number().nullable(),
  image: Yup.mixed().nullable()
});

function EditCategory() {
  type DataType = {
    parent_id: number | null;
    name: string;
    price: number;
    penalty_price: number;
    cover_image_id?: number | null;
    image?: File | null;
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

  const { data: category, isLoading: isLoadingSingle } = useQuery(
    ['category', id],
    () => Category.get(id!),
    { enabled: typeof id !== 'undefined' }
  );

  const { data: categories, isLoading } = useQuery(['categories'], () =>
    Category.all()
  );

  useEffect(() => {
    if (category?.coverImage?.url) {
      const media = new Media({
        id: category.coverImage.id,
        name: category.coverImage.name,
        path: category.coverImage.path,
        url: category.coverImage.url
      });
      setImagePreview(media.fullUrl);
    }
  }, [category]);

  const initialValues: DataType = useMemo(
    () => ({
      parent_id: category?.parentId ?? null,
      name: category?.name ?? '',
      price: category?.price ?? 0,
      penalty_price: category?.penaltyPrice ?? 0,
      cover_image_id: category?.coverImage?.id ?? null,
      image: null
    }),
    [category]
  );

  if (isLoadingSingle || isLoading) {
    return <h1>Loading...</h1>;
  }

  return (
    <>
      <h1 className="cs-title">Izmena kategorije</h1>
      <Row>
        <Col>
          <Formik
            initialValues={initialValues}
            validationSchema={categoryValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              const updatedValues = { ...values };
              if (!updatedValues.image) {
                delete updatedValues.image;
              }
              category
                ?.update(updatedValues)
                .then(() => {
                  client.invalidateQueries(['categories']);
                  client.invalidateQueries(['category', id]);
                  toast.success('Uspešno izmenjena kategorija'); // Success toast
                  navigate('/admin/categories/list');
                })
                .catch((error) => {
                  toast.error(
                    error?.response?.data?.message ||
                      'Greška pri izmeni kategorije.' // Error toast
                  );
                })
                .finally(() => {
                  setSubmitting(false);
                });
            }}
          >
            {({ setFieldValue, isSubmitting, values }) => (
              <Form autoComplete="off" className="cs-form-wrapper">
                <div className={style.form}>
                  <div className={style.formCol}>
                    <FormGroup>
                      <FormLabel>Bazna Kategorija</FormLabel>
                      <TreeSelect
                        style={{ width: '100%' }}
                        showSearch
                        treeDefaultExpandAll
                        placeholder="roditelj kategorija"
                        treeData={categories ?? []}
                        value={values.parent_id} // Bind to Formik's values
                        onChange={(value) => {
                          setFieldValue('parent_id', value); // Update Formik's field
                        }}
                      />
                      <ErrorMessageLabel name="parent_id" component="div" />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>Ime Kategorije</FormLabel>
                      <InputField
                        type="text"
                        name="name"
                        placeholder="Ime kategorije"
                        autoComplete="off"
                      />
                      <ErrorMessageLabel name="name" component="div" />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>
                        Cena proizvoda iz kategorije (
                        {process.env.REACT_APP_DEFAULT_CURRENCY})
                      </FormLabel>
                      <InputField
                        type="number"
                        step={1}
                        min={0}
                        name="price"
                        placeholder="Cena"
                        autoComplete="off"
                      />
                      <ErrorMessageLabel name="price" component="div" />
                    </FormGroup>
                    <FormGroup>
                      <FormLabel>
                        Kaznena cena za oštećenje (
                        {process.env.REACT_APP_DEFAULT_CURRENCY})
                      </FormLabel>
                      <InputField
                        type="number"
                        step={1}
                        min={0}
                        name="penalty_price"
                        placeholder="Kaznena cena"
                        autoComplete="off"
                      />
                      <ErrorMessageLabel name="penalty_price" component="div" />
                    </FormGroup>
                  </div>
                  <div className={style.formCol}>
                    <FormGroup>
                      <FormLabel>Dodaj sliku</FormLabel>
                    </FormGroup>
                    {imagePreview ? (
                      <>
                        <FormGroup className={style.imagePreview}>
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className={style.image}
                          />
                          <button
                            type="button"
                            className={style.removeImage}
                            onClick={() => {
                              setImagePreview(null);
                              setFieldValue('cover_image_id', null);
                            }}
                          >
                            <TrashIcon />
                          </button>
                        </FormGroup>
                        <div className={style.editImageButtonWrapper}>
                          <ButtonPrimary
                            type="button"
                            onClick={() => setIsMediaLibraryOpen(true)}
                            className={style.editImageButton}
                          >
                            Izmeni sliku
                          </ButtonPrimary>
                        </div>
                      </>
                    ) : (
                      <div className={style.imagePreviewEmpty}>
                        <p>Niste izabrali sliku</p>
                        <button
                          type="button"
                          className={style.addImageButton}
                          onClick={() => setIsMediaLibraryOpen(true)}
                        >
                          <PlusIcon />
                        </button>
                      </div>
                    )}
                    {isMediaLibraryOpen && (
                      <MediaLibrarySelector
                        isOpen={isMediaLibraryOpen}
                        onClose={() => setIsMediaLibraryOpen(false)}
                        onSelect={(media) => {
                          // With updated selector, this is always a single Media object
                          if (media && 'id' in media) {
                            setFieldValue('cover_image_id', media.id);
                            // Optional: for preview
                            setImagePreview(media.fullUrl ?? media.url);
                            setIsMediaLibraryOpen(false);
                          } else {
                            console.error('Selected image is not valid', media);
                          }
                        }}
                      />
                    )}
                    <ErrorMessageLabel name="cover_image_id" />
                  </div>
                </div>
                <div className={style.footer}>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>
                    Sačuvaj
                  </ButtonPrimary>
                </div>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </>
  );
}

export default EditCategory;
